import React, { useState, useEffect, useRef } from 'react';
import { fetchAllKategory, fetchAllMainCategory, postCategory, updateCategory, deleteCategoryById } from '../../../http/KategoryApi';
import AdminPageHeader from '../shared/components/AdminPageHeader';
import { AdminTable } from '../shared/components/AdminTable';
import CategoryTableRow from './components/categoryTableRow/CategoryTableRow';
import CategoryModal from './components/categoryModal/CategoryModal';
import "./CategoryTable.scss";
import Loader from '../../../components/loader/Loader';

const CategoryTable = () => {
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [mainCategories, setMainCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [selectedFilterMainCategory, setSelectedFilterMainCategory] = useState('');

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        imageFile: null,
        imageUrl: '',
        parentId: '',
        mainKategoryId: '',
        categoryIndex: '',
        seo_desc: '',
        seo_title: '',
        alias: ''
    });
    const fileInputRef = useRef(null);

    const [modifiedCategories, setModifiedCategories] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const hasChanges = Object.keys(modifiedCategories).length > 0;

    useEffect(() => {
        loadCategories();
        loadMainCategories();
    }, []);

    useEffect(() => {
        let result = categories.filter(category => {
            const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
            const catParent = category.parentId !== undefined ? category.parentId : category.mainKategoryId;
            const matchesMainCategory = selectedFilterMainCategory === '' || catParent === parseInt(selectedFilterMainCategory);
            
            return matchesSearch && matchesMainCategory;
        });

        result = result.map(category => {
            if (modifiedCategories[category.id]) {
                return { ...category, ...modifiedCategories[category.id] };
            }
            return category;
        });

        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'id') {
                    aValue = getMainCategoryName(a.id);
                    bValue = getMainCategoryName(b.id);
                } else if (sortConfig.key === 'categoryIndex') {
                    aValue = parseInt(a.categoryIndex) || 0;
                    bValue = parseInt(b.categoryIndex) || 0;
                }

                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredCategories(result);
    }, [searchTerm, selectedFilterMainCategory, categories, sortConfig, mainCategories, modifiedCategories]);

    const loadCategories = async () => {
        try {
            const data = await fetchAllKategory();
            // Защита от null/undefined
            setCategories(Array.isArray(data) ? data : []);
            setModifiedCategories({}); 
        } catch (error) {
            console.error('Error loading categories:', error);
            setCategories([]);
        }
    };

    const loadMainCategories = async () => {
        try {
            const data = await fetchAllMainCategory();
            const safeData = Array.isArray(data) ? data : [];
            setMainCategories(safeData);
            if (safeData.length > 0 && !editingCategory) {
                setFormData(prev => ({ 
                    ...prev, 
                    parentId: safeData[0].id,
                    mainKategoryId: safeData[0].id 
                }));
            }
        } catch (error) {
            console.error('Error loading main categories:', error);
            setMainCategories([]);
        }
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterMainCategoryChange = (e) => {
        setSelectedFilterMainCategory(e.target.value);
    };

    const handleQuickEdit = (categoryId, field, newValue) => {
        setModifiedCategories(prev => {
            const categoryChanges = prev[categoryId] || {};
            const originalCategory = categories.find(c => c.id === categoryId);
            const newChanges = { ...categoryChanges, [field]: newValue };

            if (originalCategory && String(originalCategory[field]) === String(newValue)) {
                delete newChanges[field];
            }

            if (Object.keys(newChanges).length === 0) {
                const newState = { ...prev };
                delete newState[categoryId];
                return newState;
            }

            return { ...prev, [categoryId]: newChanges };
        });
    };

    const cancelChanges = () => {
        if (window.confirm('Отменить все несохраненные изменения в таблице?')) {
            setModifiedCategories({});
        }
    };

    const handleApplyChanges = async () => {
        setIsSaving(true);
        try {
            const updatePromises = Object.keys(modifiedCategories).map(categoryId => {
                const changes = modifiedCategories[categoryId];
                const myFormData = new FormData();

                if (changes.categoryIndex !== undefined) myFormData.append("categoryIndex", changes.categoryIndex);

                return updateCategory(categoryId, myFormData);
            });

            await Promise.all(updatePromises);
            setModifiedCategories({});
            await loadCategories();
            setTimeout(() => { alert("Изменения успешно сохранены!") }, 200);
        } catch (error) {
            console.error(error);
            alert("Ошибка при сохранении изменений");
        } finally {
            setIsSaving(false);
        }
    };

    const openAddModal = () => {
        setEditingCategory(null);
        const defaultParent = mainCategories.length > 0 ? mainCategories[0].id : '';
        setFormData({
            name: '',
            imageFile: null,
            seo_title: '',
            alias: '',
            seo_desc: '',
            imageUrl: '',
            parentId: defaultParent,
            mainKategoryId: defaultParent,
            categoryIndex: ''
        });
        setIsModalOpen(true);
    };

    const openEditModal = (category) => {
        setEditingCategory(category);
        const currentParent = category.parentId !== undefined ? category.parentId : (category.mainKategoryId || '');
        setFormData({
            name: category.name || '',
            imageFile: null,
            seo_title: category.seo_title || '',
            alias: category.alias || '',
            seo_desc: category.seo_desc || '',
            imageUrl: category.image ? `${process.env.REACT_APP_API_URL}static/images/${category.image}` : '',
            parentId: currentParent,
            mainKategoryId: currentParent,
            categoryIndex: category.categoryIndex || ''
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
    };

    const confirmAndCloseModal = () => {
        if (window.confirm('Хотите ли вы закрыть форму? Несохраненные данные будут потеряны.')) {
            closeModal();
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: value,
            ...(name === 'parentId' ? { mainKategoryId: value } : {}),
            ...(name === 'mainKategoryId' ? { parentId: value } : {})
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData(prev => ({
                    ...prev,
                    imageFile: file,
                    imageUrl: event.target.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const myFormData = new FormData();
            myFormData.append("seo_title", formData.seo_title || '');
            myFormData.append("alias", formData.alias || '');
            myFormData.append("seo_desc", formData.seo_desc || '');
            myFormData.append("name", formData.name || '');
            if (formData.imageFile) {
                myFormData.append("image", formData.imageFile);
            }
            if (formData.categoryIndex !== undefined && formData.categoryIndex !== '') {
                myFormData.append("categoryIndex", formData.categoryIndex);
            }

            const pId = formData.parentId || formData.mainKategoryId || 0;
            myFormData.append("parentId", pId);
            myFormData.append("mainKategoryId", pId);
            
            if (editingCategory) {
                await updateCategory(editingCategory.id, myFormData);
            } else {
                await postCategory(myFormData);
            }
            closeModal();
            await loadCategories();
        } catch (error) {
            console.error(error);
            alert("Ошибка при сохранении категории");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmitWithoutClose = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const myFormData = new FormData();
            myFormData.append("seo_title", formData.seo_title || '');
            myFormData.append("seo_desc", formData.seo_desc || '');
            myFormData.append("alias", formData.alias || '');
            myFormData.append("name", formData.name || '');
            if (formData.imageFile) {
                myFormData.append("image", formData.imageFile);
            }
            if (formData.categoryIndex !== undefined && formData.categoryIndex !== '') {
                myFormData.append("categoryIndex", formData.categoryIndex);
            }

            const pId = formData.parentId || formData.mainKategoryId || 0;
            myFormData.append("parentId", pId);
            myFormData.append("mainKategoryId", pId);

            if (editingCategory) {
                await updateCategory(editingCategory.id, myFormData);
            } else {
                await postCategory(myFormData);
            }
            await loadCategories();
        } catch (error) {
            console.error(error);
            alert("Ошибка при сохранении");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
            setIsSaving(true);
            try {
                await deleteCategoryById(id);
                await loadCategories();
            } catch (error) {
                console.error(error);
                alert("Ошибка при удалении");
            } finally {
                setIsSaving(false);
            }
        }
    };

    const getMainCategoryName = (parentId) => {
        const mainCat = mainCategories.find(cat => cat.id === parentId);
        // console.log(mainCategories);
        return mainCat ? mainCat.name : 'Unknown';
    };

    const subCategories = filteredCategories.filter(c => c.parentId != 0);

    const COLUMNS = [
        { label: 'Название', sortKey: 'name' },
        { label: 'Картинка', width: '110px' },
        { label: 'Главная категория', sortKey: 'parentId' },
        { label: 'Индекс', sortKey: 'categoryIndex', width: '130px' },
        { label: 'Действия', align: 'right', width: '190px' }
    ];

    return (
        <div className="admin-page-container admin-category-editor">
            <AdminPageHeader
                title="Подкатегории"
                count={subCategories.length}
                searchTerm={searchTerm}
                onSearch={handleSearch}
                searchPlaceholder="Поиск подкатегории..."
                onAdd={openAddModal}
                addButtonText="Добавить подкатегорию"
                hasChanges={hasChanges}
                isSaving={isSaving}
                onApplyChanges={handleApplyChanges}
                onCancelChanges={cancelChanges}
            >
                <select
                    value={selectedFilterMainCategory}
                    onChange={handleFilterMainCategoryChange}
                    className="admin-form-select"
                >
                    <option value="">Все главные категории</option>
                    {mainCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </AdminPageHeader>

            <AdminTable
                columns={COLUMNS}
                data={subCategories}
                sortConfig={sortConfig}
                onSort={requestSort}
                getSortIndicator={getSortIndicator}
                isLoading={isSaving}
                emptyMessage="Подкатегории не найдены"
                renderRow={(category) => (
                    <CategoryTableRow 
                        key={category.id}
                        category={category}
                        modifiedCategory={modifiedCategories[category.id]}
                        getMainCategoryName={getMainCategoryName}
                        handleQuickEdit={handleQuickEdit}
                        openEditModal={openEditModal}
                        handleDelete={handleDelete}
                    />
                )}
            />

            <CategoryModal 
                isModalOpen={isModalOpen}
                confirmAndCloseModal={confirmAndCloseModal}
                editingCategory={editingCategory}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                handleSubmitWithoutClose={handleSubmitWithoutClose}
                mainCategories={mainCategories}
                getMainCategoryName={getMainCategoryName}
                triggerFileInput={triggerFileInput}
                fileInputRef={fileInputRef}
                handleFileChange={handleFileChange}
            />

            {/* Глобальный лоадер */}
            <Loader isVisible={isSaving} text="Обновление категорий..." />
        </div>
    );
};

export default CategoryTable;